import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { boardsAPI, tasksAPI, clientsAPI, usersAPI, chatAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { ArrowLeft, FileDown, Users2, X, Search, ChevronDown, ChevronRight, Plus, Pencil, Trash2, Check, Copy, MessageSquare, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { getDueDate, isOverdue, isDueSoon, formatDueDate } from '@/lib/due-dates';
import { formatClientWithGstin } from '@/lib/gstin-utils';
import { isPeriodActiveForGstin, inactiveReason } from '@/lib/gstin-period';
import SortableHeader, { sortBy } from '@/components/SortableHeader';
import ClientPickerDialog from '@/components/ClientPickerDialog';
import { directorsAPI } from '@/lib/api';
import { FREQ_MIN_WIDTHS_SM, FREQ_COL_WIDTHS_SM, dueDateClass } from '@/lib/compliance-utils';
import ReminderDialog from './components/ReminderDialog';
import ChecklistGrid from './components/ChecklistGrid';
import OneTimeInstanceDialog from './components/OneTimeInstanceDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const STAGE_LABELS = {
  not_started: 'Not Started',
  documents_pending: 'Docs Pending',
  documents_received: 'Docs Received',
  further_docs_required: 'Further Docs Required',
  ready_for_review: 'Ready for Review',
  tax_payable: 'Tax Payable',
  ready_to_file: 'Ready to File',
  filed: 'Filed',
};

const STAGE_COLORS = {
  not_started: 'bg-gray-100 text-gray-700 border-gray-200',
  documents_pending: 'bg-amber-50 text-amber-700 border-amber-200',
  documents_received: 'bg-blue-50 text-blue-700 border-blue-200',
  further_docs_required: 'bg-orange-50 text-orange-700 border-orange-200',
  ready_for_review: 'bg-purple-50 text-purple-700 border-purple-200',
  tax_payable: 'bg-rose-50 text-rose-700 border-rose-200',
  ready_to_file: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  filed: 'bg-green-50 text-green-700 border-green-200',
};

const SUBTASK_TEMPLATES = {
  gst_return: {
    label: 'GST Returns',
    items: [
      { title: 'GSTR-3B', frequency: 'monthly' },
      { title: 'GSTR-1', frequency: 'monthly' },
      { title: 'CMP-08', frequency: 'quarterly' },
      { title: 'GSTR-4', frequency: 'annual' },
      { title: 'GSTR-6', frequency: 'monthly' },
      { title: 'GSTR-7', frequency: 'monthly' },
      { title: 'GSTR-9', frequency: 'annual' },
      { title: 'GSTR-9C', frequency: 'annual' },
    ],
  },
  tds_return: {
    label: 'TDS Returns',
    items: [
      { title: 'Form 24Q (Salary TDS)', frequency: 'quarterly' },
      { title: 'Form 26Q (Non-Salary TDS)', frequency: 'quarterly' },
      { title: 'Form 27Q (NRI TDS)', frequency: 'quarterly' },
      { title: 'Form 27EQ (TCS)', frequency: 'quarterly' },
    ],
  },
  income_tax: {
    label: 'Income Tax',
    items: [
      { title: 'ITR Filing', frequency: 'annual' },
      { title: 'Advance Tax - Q1', frequency: 'quarterly' },
      { title: 'Advance Tax - Q2', frequency: 'quarterly' },
      { title: 'Advance Tax - Q3', frequency: 'quarterly' },
      { title: 'Advance Tax - Q4', frequency: 'quarterly' },
      { title: 'Tax Audit (44AB)', frequency: 'annual' },
    ],
  },
  audit: {
    label: 'Audit',
    items: [
      { title: 'Statutory Audit', frequency: 'annual' },
      { title: 'Tax Audit', frequency: 'annual' },
      { title: 'Internal Audit', frequency: 'quarterly' },
      { title: 'GST Audit', frequency: 'annual' },
      { title: 'Stock Audit', frequency: 'annual' },
    ],
  },
  roc_compliance: {
    label: 'ROC Compliance',
    items: [
      { title: 'AOC-4 (Financials)', frequency: 'annual' },
      { title: 'MGT-7 (Annual Return)', frequency: 'annual' },
      { title: 'DIR-3 KYC', frequency: 'annual' },
      { title: 'Board Resolutions', frequency: 'quarterly' },
      { title: 'Charge Filing', frequency: 'annual' },
    ],
  },
  advisory: {
    label: 'Advisory',
    items: [
      { title: 'Tax Planning', frequency: 'annual' },
      { title: 'FEMA Compliance', frequency: 'annual' },
      { title: 'Business Structuring', frequency: 'annual' },
      { title: 'Valuation Report', frequency: 'annual' },
      { title: 'ESOP Structuring', frequency: 'annual' },
    ],
  },
};

const STATUS_SHORT = {
  not_started: '-',
  documents_pending: 'DP',
  documents_received: 'DR',
  further_docs_required: 'FD',
  ready_for_review: 'RR',
  tax_payable: 'TP',
  ready_to_file: 'RF',
  filed: 'F',
};

const STATUS_CELL_COLORS = {
  not_started: 'bg-gray-50 text-gray-400',
  documents_pending: 'bg-amber-50 text-amber-700',
  documents_received: 'bg-blue-50 text-blue-700',
  further_docs_required: 'bg-orange-50 text-orange-700',
  ready_for_review: 'bg-purple-50 text-purple-700',
  tax_payable: 'bg-rose-50 text-rose-700',
  ready_to_file: 'bg-indigo-50 text-indigo-700',
  filed: 'bg-green-100 text-green-700',
};

const DEFAULT_STAGES = ['not_started', 'documents_pending', 'documents_received', 'ready_for_review', 'ready_to_file', 'filed'];

const FREQ_BADGE = {
  monthly: { label: 'Monthly', color: 'bg-blue-50 text-blue-600 border-blue-200' },
  quarterly: { label: 'Quarterly', color: 'bg-amber-50 text-amber-600 border-amber-200' },
  half_yearly: { label: 'Half Yearly', color: 'bg-teal-50 text-teal-600 border-teal-200' },
  annual: { label: 'Annual', color: 'bg-purple-50 text-purple-600 border-purple-200' },
  annually: { label: 'Annual', color: 'bg-purple-50 text-purple-600 border-purple-200' },
  '3_yearly': { label: '3-Yearly', color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  one_time: { label: 'One Time', color: 'bg-gray-50 text-gray-600 border-gray-200' },
};
const DEFAULT_FREQ_BADGE = { label: 'Other', color: 'bg-gray-50 text-gray-500 border-gray-200' };

const DEFAULT_FY = '2026-27';
const DEFAULT_FY_START = 2026;

const getMonthlyPeriods = (fyStart) => {
  const start = fyStart || DEFAULT_FY_START;
  const periods = [];
  for (let m = 4; m <= 12; m++) periods.push(`${start}-${String(m).padStart(2, '0')}`);
  for (let m = 1; m <= 3; m++) periods.push(`${start + 1}-${String(m).padStart(2, '0')}`);
  return periods;
};

const getQuarterlyPeriods = (fyStart) => {
  const start = fyStart || DEFAULT_FY_START;
  return [`${start}-Q1`, `${start}-Q2`, `${start}-Q3`, `${start}-Q4`];
};
const getAnnualPeriods = (fy) => [`FY${fy || DEFAULT_FY}`];

const getPeriodLabel = (period) => {
  if (period.includes('H1')) return 'H1 (Apr-Sep)';
  if (period.includes('H2')) return 'H2 (Oct-Mar)';
  if (period.includes('Q')) {
    const q = period.split('-')[1];
    const labels = { Q1: 'Q1 (Apr-Jun)', Q2: 'Q2 (Jul-Sep)', Q3: 'Q3 (Oct-Dec)', Q4: 'Q4 (Jan-Mar)' };
    return labels[q] || q;
  }
  if (period.startsWith('FY')) return `FY ${period.replace('FY', '')}`;
  if (period.startsWith('OT-')) return 'One Time';
  const month = parseInt(period.split('-')[1]);
  const year = period.split('-')[0].slice(2);
  const names = { 1: 'Jan', 2: 'Feb', 3: 'Mar', 4: 'Apr', 5: 'May', 6: 'Jun', 7: 'Jul', 8: 'Aug', 9: 'Sep', 10: 'Oct', 11: 'Nov', 12: 'Dec' };
  return `${names[month]}'${year}`;
};

const getPeriodsForFrequency = (freq, fy) => {
  const fyStr = fy || DEFAULT_FY;
  const fyStart = parseInt(fyStr.split('-')[0]);
  if (freq === 'quarterly') return getQuarterlyPeriods(fyStart);
  if (freq === 'half_yearly') return [`${fyStart}-H1`, `${fyStart}-H2`];
  if (freq === 'annual' || freq === 'annually' || freq === '3_yearly') return getAnnualPeriods(fyStr);
  if (freq === 'one_time') return [`OT-${fyStr}`];
  return getMonthlyPeriods(fyStart);
};

const BoardDetailPage = () => {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [board, setBoard] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [directors, setDirectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterStaff, setFilterStaff] = useState('');
  const [filterFrequency, setFilterFrequency] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedSubtasks, setExpandedSubtasks] = useState(new Set());
  const [expandedFilters, setExpandedFilters] = useState({});
  const [expandedSorts, setExpandedSorts] = useState({}); // per-subtask sort state
  const [clientPickerOpen, setClientPickerOpen] = useState(null);
  // Phase 2 — kind-aware Add Instance dialog for GST One-Time Activities
  const [oneTimeDialog, setOneTimeDialog] = useState(null); // { taskId, subtaskId, kind } | null
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
  const [newSubtaskFreq, setNewSubtaskFreq] = useState('');
  const [addSubtaskForTask, setAddSubtaskForTask] = useState(null);
  const [staffMembers, setStaffMembers] = useState([]);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingTaskTitle, setEditingTaskTitle] = useState('');
  const [editingSubtaskId, setEditingSubtaskId] = useState(null);
  const [editingSubtaskTitle, setEditingSubtaskTitle] = useState('');
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderTarget, setReminderTarget] = useState(null);
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', fy_year: '2026-27', default_frequency: 'monthly' });

  const canManage = user?.role === 'admin' || user?.role === 'staff';
  const isAdmin = user?.role === 'admin';

  const loadData = useCallback(async () => {
    try {
      const [boardRes, tasksRes, clientsRes, staffRes, directorsRes] = await Promise.all([
        boardsAPI.get(boardId),
        tasksAPI.list({ board_id: boardId }),
        canManage ? clientsAPI.list() : Promise.resolve({ data: [] }),
        canManage ? usersAPI.list({ role: 'staff' }) : Promise.resolve({ data: [] }),
        canManage ? directorsAPI.list().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setBoard(boardRes.data);
      setTasks(tasksRes.data);
      setClients(clientsRes.data);
      setStaffMembers(staffRes.data || []);
      setDirectors(directorsRes.data || []);
    } catch (err) {
      toast.error('Failed to load board data');
    } finally {
      setLoading(false);
    }
  }, [boardId, canManage]);

  useEffect(() => { loadData(); }, [loadData]);

  const allSubtasks = useMemo(() => {
    const result = [];
    tasks.forEach(task => {
      (task.subtasks || []).forEach(st => {
        result.push({ ...st, taskId: task.id, taskTitle: task.title });
      });
    });
    return result;
  }, [tasks]);

  const filteredSubtasks = useMemo(() => {
    return allSubtasks.filter(st => {
      // Text search on subtask/task title
      if (search.trim()) {
        const q = search.toLowerCase();
        if (!st.title.toLowerCase().includes(q) && !st.taskTitle.toLowerCase().includes(q)) return false;
      }
      // Client name / company / GSTIN / PAN filter (matches if ANY assigned client matches)
      const cQ = filterClient.trim().toLowerCase();
      if (cQ) {
        const hasMatch = (st.clients || []).some(c => {
          const cl = clients.find(x => x.id === c.client_id);
          if (!cl) return false;
          const fields = [cl.name || '', cl.company_name || '', c.gstin || '', cl.pan || ''];
          return fields.some(f => f.toLowerCase().includes(cQ));
        });
        if (!hasMatch) return false;
      }
      // Staff filter
      if (filterStaff && filterStaff !== 'all') {
        const matchSubtaskStaff = st.assigned_to === filterStaff;
        const matchClientStaff = (st.clients || []).some(c => c.assigned_to === filterStaff);
        if (!matchSubtaskStaff && !matchClientStaff) return false;
      }
      // Frequency filter
      if (filterFrequency && filterFrequency !== 'all') {
        const freq = st.frequency || 'monthly';
        if (freq !== filterFrequency) return false;
      }
      // Status filter — keep subtask only if at least one client cell has the
      // selected status across any of its periods. Lets users zoom in on
      // "show me only Mismatched / In Process / Tax Payable" rows.
      if (filterStatus && filterStatus !== 'all') {
        const hasMatchingStatus = (st.clients || []).some(c => {
          const periods = c.periods || {};
          return Object.values(periods).some(s => s === filterStatus);
        });
        if (!hasMatchingStatus) return false;
      }
      return true;
    });
  }, [allSubtasks, search, filterClient, filterStaff, filterFrequency, filterStatus, clients]);

  const useCompanyName = board && /GST|TDS|Tax Audit|Statutory Audit|Trust Audit/i.test(board.name);
  const getEntityName = (subtask, entityId) => {
    if (subtask?.entity_type === 'director') {
      const d = directors.find(x => x.id === entityId);
      return d ? `${d.name}${d.din ? ' · ' + d.din : ''}` : '?';
    }
    const cl = clients.find(c => c.id === entityId);
    if (!cl) return '?';
    if (useCompanyName) return cl.company_name || cl.name || '?';
    return cl.name || '?';
  };
  // Backwards-compatible alias for non-director paths still using getClientName(id)
  const getClientName = (clientId) => {
    const cl = clients.find(c => c.id === clientId);
    if (!cl) return '?';
    if (useCompanyName) return cl.company_name || cl.name || '?';
    return cl.name || '?';
  };
  const getClientDisplayName = (clientId, gstin) => formatClientWithGstin(getClientName(clientId), gstin);

  const getClientGstinObj = (clientId, gstinNumber) => {
    if (!gstinNumber) return null;
    const cl = clients.find(c => c.id === clientId);
    if (!cl || !Array.isArray(cl.gstins)) return null;
    return cl.gstins.find(g => (typeof g === 'object' ? g.number : g) === gstinNumber) || null;
  };

  const toggleExpand = (subtaskId) => {
    setExpandedSubtasks(prev => {
      const next = new Set(prev);
      if (next.has(subtaskId)) next.delete(subtaskId); else next.add(subtaskId);
      return next;
    });
  };

  const handlePeriodStatusChange = async (taskId, subtaskId, clientId, period, newStatus) => {
    try {
      await tasksAPI.updateSubtaskClientPeriodStatus(taskId, subtaskId, clientId, period, { status: newStatus });
      await loadData();
    } catch (err) { toast.error('Failed to update status'); }
  };

  const handleClientToggle = async (taskId, subtask, clientId) => {
    const currentClientIds = (subtask.clients || []).map(c => c.client_id);
    const newClientIds = currentClientIds.includes(clientId)
      ? currentClientIds.filter(id => id !== clientId)
      : [...currentClientIds, clientId];
    try {
      await tasksAPI.updateSubtask(taskId, subtask.id, { client_ids: newClientIds });
      await loadData();
    } catch (err) { toast.error('Failed to update client'); }
  };

  const handleApplyClientPickerBoard = async (selectedIds) => {
    if (!clientPickerOpen) return;
    const { taskId, subtaskId } = clientPickerOpen;
    try {
      await tasksAPI.updateSubtask(taskId, subtaskId, { client_ids: selectedIds });
      await loadData();
      toast.success('Client list updated');
    } catch (err) { toast.error('Failed to update client list'); }
  };

  const handleSubtaskStaffAssign = async (taskId, subtaskId, staffId, force = false) => {
    try {
      const { data } = await tasksAPI.assignSubtaskStaff(taskId, subtaskId, { assigned_to: staffId || null, force });
      toast.success(data?.message || (staffId ? 'Default team member set' : 'Default unset'));
      await loadData();
    } catch (err) { toast.error('Failed to assign team member'); }
  };

  const handleUpdateSubtaskFrequency = async (taskId, subtaskId, newFreq) => {
    try {
      await tasksAPI.updateSubtask(taskId, subtaskId, { frequency: newFreq });
      toast.success(`Frequency updated to ${newFreq}`);
      await loadData();
    } catch (err) { toast.error('Failed to update frequency'); }
  };

  const handleRenameTask = async (taskId) => {
    const title = editingTaskTitle.trim();
    if (!title) return;
    try {
      await tasksAPI.update(taskId, { title });
      toast.success('Task renamed');
      setEditingTaskId(null);
      await loadData();
    } catch (err) { toast.error('Failed to rename task'); }
  };

  const handleUpdateTaskField = async (taskId, field, value) => {
    try {
      await tasksAPI.update(taskId, { [field]: value });
      toast.success(`Task ${field.replace('_', ' ')} updated`);
      await loadData();
    } catch (err) { toast.error(`Failed to update ${field}`); }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!newTask.title.trim()) return;
    try {
      await tasksAPI.create({
        title: newTask.title.trim(),
        board_id: boardId,
        fy_year: newTask.fy_year,
        default_frequency: newTask.default_frequency,
      });
      toast.success('Task created');
      setCreateTaskOpen(false);
      setNewTask({ title: '', fy_year: '2026-27', default_frequency: 'monthly' });
      await loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create task'); }
  };

  const handleRenameSubtask = async (taskId, subtaskId) => {
    const title = editingSubtaskTitle.trim();
    if (!title) return;
    try {
      await tasksAPI.updateSubtask(taskId, subtaskId, { title });
      toast.success('Subtask renamed');
      setEditingSubtaskId(null);
      await loadData();
    } catch (err) { toast.error('Failed to rename subtask'); }
  };

  const handleDeleteSubtask = async (taskId, subtaskId, subtaskTitle) => {
    if (!window.confirm(`Delete subtask "${subtaskTitle}"? This will remove all client data for this subtask.`)) return;
    try {
      await tasksAPI.deleteSubtask(taskId, subtaskId);
      toast.success('Subtask deleted');
      await loadData();
    } catch (err) { toast.error('Failed to delete subtask'); }
  };

  const handleCloneTask = async (taskId, taskTitle) => {
    if (!window.confirm(`Clone "${taskTitle}" to the next financial year? All subtasks, client assignments, and team member assignments will be copied. Period statuses and notes will be reset.`)) return;
    try {
      const res = await tasksAPI.clone(taskId);
      toast.success(`Cloned as "${res.data.title}" with ${(res.data.subtasks || []).length} subtasks`);
      await loadData();
    } catch (err) { toast.error('Failed to clone task'); }
  };

  const handleDeleteTask = async (taskId, taskTitle) => {
    try {
      await tasksAPI.delete(taskId);
      toast.success(`Task "${taskTitle}" deleted`);
      await loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete task'); }
  };

  const handleOpenTaskChat = async (clientId) => {
    const clientName = getClientName(clientId) || 'Client';
    try {
      const res = await chatAPI.getOrCreateTaskThread({
        board_id: boardId,
        client_id: clientId,
        board_name: board?.name || 'Board',
        client_name: clientName,
      });
      navigate(`/dashboard/chat?group=${res.data.id}`);
    } catch (err) { toast.error('Failed to open chat thread'); }
  };

  const handleClientStaffAssign = async (taskId, subtaskId, clientId, staffId) => {
    try {
      await tasksAPI.assignClientStaff(taskId, subtaskId, clientId, { assigned_to: staffId || null });
      await loadData();
    } catch (err) { toast.error('Failed to assign team member'); }
  };

  const getStaffName = (staffId) => {
    if (!staffId) return null;
    const s = staffMembers.find(m => m.id === staffId);
    return s?.name || s?.email || null;
  };

  const handleLoadTemplate = async (taskId, templateKey) => {
    const template = SUBTASK_TEMPLATES[templateKey];
    if (!template) return;
    try {
      const res = await tasksAPI.addSubtasksBulk(taskId, { items: template.items });
      if (res.data.created > 0) toast.success(`${res.data.created} subtasks added from "${template.label}" template`);
      else toast.info('All template subtasks already exist');
      await loadData();
    } catch (err) { toast.error('Failed to load template'); }
  };

  const handleAddSubtask = async (taskId) => {
    if (!newSubtaskTitle.trim()) return;
    try {
      const payload = { title: newSubtaskTitle.trim(), client_ids: [] };
      if (newSubtaskFreq) payload.frequency = newSubtaskFreq;
      await tasksAPI.addSubtask(taskId, payload);
      toast.success('Subtask added');
      setNewSubtaskTitle('');
      setNewSubtaskFreq('');
      setAddSubtaskForTask(null);
      await loadData();
    } catch (err) { toast.error('Failed to add subtask'); }
  };

  const taskSummary = useMemo(() => {
    return tasks.map(task => ({
      ...task,
      subtaskCount: (task.subtasks || []).length,
      totalClients: (task.subtasks || []).reduce((sum, st) => sum + (st.clients || []).length, 0),
    }));
  }, [tasks]);

  if (loading) return <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="h-16 bg-muted rounded-xl animate-pulse" />)}</div>;

  if (!board) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Board not found</p>
        <Button variant="ghost" onClick={() => navigate('/dashboard/boards')} className="mt-4"><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="board-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/boards')} className="h-8 w-8 p-0" data-testid="back-to-boards">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold" data-testid="board-title">{board.name}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">{allSubtasks.length} subtasks</Badge>
          <Badge variant="secondary" className="text-xs">{tasks.length} tasks</Badge>
          {isAdmin && (
            <Dialog open={createTaskOpen} onOpenChange={setCreateTaskOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="create-task-button">
                  <Plus className="h-4 w-4 mr-1" /> New Task
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader><DialogTitle>Create Task</DialogTitle></DialogHeader>
                <form onSubmit={handleCreateTask} className="space-y-4">
                  <div>
                    <Label>Task Title *</Label>
                    <Input value={newTask.title} onChange={e => setNewTask({...newTask, title: e.target.value})} placeholder="e.g. Accounting FY 2026-27" required data-testid="new-task-title" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Financial Year</Label>
                      <Select value={newTask.fy_year} onValueChange={v => setNewTask({...newTask, fy_year: v})}>
                        <SelectTrigger data-testid="new-task-fy"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="2024-25">FY 2024-25</SelectItem>
                          <SelectItem value="2025-26">FY 2025-26</SelectItem>
                          <SelectItem value="2026-27">FY 2026-27</SelectItem>
                          <SelectItem value="2027-28">FY 2027-28</SelectItem>
                          <SelectItem value="2028-29">FY 2028-29</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Default Frequency</Label>
                      <Select value={newTask.default_frequency} onValueChange={v => setNewTask({...newTask, default_frequency: v})}>
                        <SelectTrigger data-testid="new-task-freq"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                          <SelectItem value="half_yearly">Half Yearly</SelectItem>
                          <SelectItem value="annual">Annual</SelectItem>
                          <SelectItem value="one_time">One Time</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button type="submit" className="w-full" data-testid="submit-create-task">Create Task</Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search subtasks or tasks..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="board-detail-search" />
          </div>
          {isAdmin && SUBTASK_TEMPLATES[board.board_type] && tasks.length > 0 && (
            <Select onValueChange={(taskId) => handleLoadTemplate(taskId, board.board_type)}>
              <SelectTrigger className="w-auto gap-2 text-sm" data-testid="load-template-for-task">
                <FileDown className="h-4 w-4" />
                <span>Load {SUBTASK_TEMPLATES[board.board_type].label} Template</span>
              </SelectTrigger>
              <SelectContent>
                {tasks.map(t => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        {/* Column-specific filters */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground mr-1">Filters:</span>
          <Input placeholder="Client name..." value={filterClient} onChange={e => setFilterClient(e.target.value)} className="h-8 text-xs w-40" data-testid="filter-client-name" />
          <Select value={filterStaff || 'all'} onValueChange={v => setFilterStaff(v === 'all' ? '' : v)}>
            <SelectTrigger className="h-8 text-xs w-40" data-testid="filter-staff-trigger">
              <SelectValue placeholder="All Team" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Team</SelectItem>
              {staffMembers.map(s => <SelectItem key={s.id} value={s.id}><span className="text-xs">{s.name || s.email}</span></SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterFrequency || 'all'} onValueChange={v => setFilterFrequency(v === 'all' ? '' : v)}>
            <SelectTrigger className="h-8 text-xs w-36" data-testid="filter-frequency-trigger">
              <SelectValue placeholder="All Frequencies" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Frequencies</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="half_yearly">Half Yearly</SelectItem>
              <SelectItem value="annual">Annual</SelectItem>
              <SelectItem value="one_time">One Time</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterStatus || 'all'} onValueChange={v => setFilterStatus(v === 'all' ? '' : v)}>
            <SelectTrigger className="h-8 text-xs w-44" data-testid="filter-status-trigger">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="not_started">Not Started</SelectItem>
              <SelectItem value="documents_pending">Docs Pending</SelectItem>
              <SelectItem value="documents_received">Docs Received</SelectItem>
              <SelectItem value="further_docs_required">Further Docs Required</SelectItem>
              <SelectItem value="ready_for_review">Ready for Review</SelectItem>
              <SelectItem value="in_process">In Process</SelectItem>
              <SelectItem value="tax_payable">Tax Payable</SelectItem>
              <SelectItem value="ready_to_file">Ready to File</SelectItem>
              <SelectItem value="filed">Filed</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="not_to_be_filed">Not to be Filed</SelectItem>
              <SelectItem value="tax_not_to_be_paid">Tax Not to be Paid</SelectItem>
              <SelectItem value="mismatched">Mismatched</SelectItem>
              <SelectItem value="matched">Matched</SelectItem>
            </SelectContent>
          </Select>
          {(filterClient || filterStaff || filterFrequency || filterStatus) && (
            <Button variant="ghost" size="sm" className="h-8 text-xs gap-1 text-muted-foreground hover:text-red-500" onClick={() => { setFilterClient(''); setFilterStaff(''); setFilterFrequency(''); setFilterStatus(''); }} data-testid="clear-board-filters">
              <X className="h-3.5 w-3.5" /> Clear
            </Button>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-2 text-[11px]">
        <span className="text-muted-foreground font-medium mr-1">Status:</span>
        {Object.entries(STATUS_SHORT).map(([key, short]) => (
          <span key={key} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${STAGE_COLORS[key]}`}>
            <span className="font-bold">{short}</span> = {STAGE_LABELS[key]}
          </span>
        ))}
        <span className="text-muted-foreground font-medium ml-3 mr-1">Frequency:</span>
        {Object.entries(FREQ_BADGE).map(([key, val]) => (
          <span key={key} className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-medium ${val.color}`}>{val.label}</span>
        ))}
        <span className="text-muted-foreground font-medium ml-3 mr-1">Due:</span>
        <span className="inline-flex items-center px-2 py-0.5 rounded border border-red-200 bg-red-50 text-red-600 text-[11px] font-medium">Overdue</span>
        <span className="inline-flex items-center px-2 py-0.5 rounded border border-amber-200 bg-amber-50/40 text-amber-600 text-[11px] font-medium">Due Soon</span>
      </div>

      {/* Task Sections */}
      {taskSummary.map(task => {
        const taskSubtasks = filteredSubtasks.filter(st => st.taskId === task.id);
        if (search && taskSubtasks.length === 0) return null;

        return (
          <Card key={task.id} className="overflow-hidden" data-testid={`task-section-${task.id}`}>
            <div className="flex items-center justify-between px-5 py-3 bg-muted/30 border-b">
              <div className="flex items-center gap-3">
                {isAdmin && editingTaskId === task.id ? (
                  <div className="flex items-center gap-2">
                    <Input value={editingTaskTitle} onChange={e => setEditingTaskTitle(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleRenameTask(task.id)} className="h-8 text-sm w-64 font-semibold" autoFocus data-testid={`edit-task-title-${task.id}`} />
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleRenameTask(task.id)}><Check className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditingTaskId(null)}><X className="h-4 w-4" /></Button>
                  </div>
                ) : (
                  <>
                    <h3 className="font-semibold text-sm">{task.title}</h3>
                    {isAdmin && (
                      <div className="flex items-center gap-1">
                        <button onClick={() => { setEditingTaskId(task.id); setEditingTaskTitle(task.title); }} className="text-muted-foreground hover:text-primary transition-colors p-1" data-testid={`rename-task-${task.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => handleCloneTask(task.id, task.title)} className="text-muted-foreground hover:text-teal-600 transition-colors p-1" title="Clone to Next FY" data-testid={`clone-task-${task.id}`}>
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button className="text-muted-foreground hover:text-red-500 transition-colors p-1" title="Delete Task" data-testid={`delete-task-${task.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Task?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete "<span className="font-medium">{task.title}</span>" and all its subtasks. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteTask(task.id, task.title)} className="bg-red-600 hover:bg-red-700" data-testid={`confirm-delete-task-${task.id}`}>
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </>
                )}
                <Badge variant="secondary" className="text-[11px]">{task.subtaskCount} subtasks</Badge>
                {isAdmin && (
                  <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                    <Select value={task.fy_year || DEFAULT_FY} onValueChange={v => handleUpdateTaskField(task.id, 'fy_year', v)}>
                      <SelectTrigger className="h-7 w-[110px] text-[11px]" data-testid={`task-fy-${task.id}`}>
                        <SelectValue placeholder="FY" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2024-25">FY 2024-25</SelectItem>
                        <SelectItem value="2025-26">FY 2025-26</SelectItem>
                        <SelectItem value="2026-27">FY 2026-27</SelectItem>
                        <SelectItem value="2027-28">FY 2027-28</SelectItem>
                        <SelectItem value="2028-29">FY 2028-29</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={task.default_frequency || ''} onValueChange={v => handleUpdateTaskField(task.id, 'default_frequency', v)}>
                      <SelectTrigger className="h-7 w-[120px] text-[11px]" data-testid={`task-freq-${task.id}`}>
                        <SelectValue placeholder="Default Freq" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="half_yearly">Half Yearly</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                        <SelectItem value="one_time">One Time</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              {isAdmin && (
                addSubtaskForTask === task.id ? (
                  <div className="flex items-center gap-2">
                    <Input placeholder="Subtask name..." value={newSubtaskTitle} onChange={e => setNewSubtaskTitle(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddSubtask(task.id)} className="h-8 text-sm w-40" autoFocus data-testid="new-subtask-input" />
                    <Select value={newSubtaskFreq || 'default'} onValueChange={v => setNewSubtaskFreq(v === 'default' ? '' : v)}>
                      <SelectTrigger className="h-8 text-xs w-28" data-testid="new-subtask-freq"><SelectValue placeholder="Frequency" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">Task Default</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="half_yearly">Half Yearly</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                        <SelectItem value="one_time">One Time</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="sm" className="h-8 text-xs" onClick={() => handleAddSubtask(task.id)}>Add</Button>
                    <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setAddSubtaskForTask(null); setNewSubtaskTitle(''); setNewSubtaskFreq(''); }}>Cancel</Button>
                  </div>
                ) : (
                  <Button variant="ghost" size="sm" className="h-8 text-xs gap-1" onClick={() => setAddSubtaskForTask(task.id)} data-testid="add-subtask-trigger">
                    <Plus className="h-3 w-3" /> Add Subtask
                  </Button>
                )
              )}
            </div>

            <CardContent className="p-0">
              {((search || filterClient || filterStaff || filterFrequency || filterStatus) ? taskSubtasks : (task.subtasks || []).map(st => ({ ...st, taskId: task.id, taskTitle: task.title }))).length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">No subtasks yet.</div>
              ) : (
                <div className="divide-y">
                  {((search || filterClient || filterStaff || filterFrequency || filterStatus) ? taskSubtasks : (task.subtasks || []).map(st => ({ ...st, taskId: task.id, taskTitle: task.title }))).map(subtask => {
                    const isExpanded = expandedSubtasks.has(subtask.id);
                    const subtaskClients = subtask.clients || [];
                    const totalCount = subtaskClients.length;
                    const freq = subtask.frequency || task.default_frequency || 'monthly';
                    const freqBadge = FREQ_BADGE[freq] || DEFAULT_FREQ_BADGE;
                    const periods = getPeriodsForFrequency(freq, task.fy_year);

                    return (
                      <div key={subtask.id} data-testid={`subtask-row-${subtask.id}`}>
                        <div className="flex items-center gap-4 px-5 py-3 hover:bg-muted/20 transition-colors cursor-pointer" onClick={() => toggleExpand(subtask.id)}>
                          {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
                          {isAdmin && editingSubtaskId === subtask.id ? (
                            <div className="flex items-center gap-2 flex-1 min-w-0" onClick={e => e.stopPropagation()}>
                              <Input value={editingSubtaskTitle} onChange={e => setEditingSubtaskTitle(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleRenameSubtask(subtask.taskId, subtask.id)} className="h-7 text-sm w-48" autoFocus data-testid={`edit-subtask-title-${subtask.id}`} />
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleRenameSubtask(subtask.taskId, subtask.id)}><Check className="h-3.5 w-3.5" /></Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditingSubtaskId(null)}><X className="h-3.5 w-3.5" /></Button>
                            </div>
                          ) : (
                            <>
                              <span
                                className="font-medium text-sm flex-1 min-w-0 hover:text-primary hover:underline"
                                onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/boards/${boardId}/task/${subtask.taskId}/subtask/${subtask.id}`); }}
                                data-testid={`subtask-link-${subtask.id}`}
                              >{subtask.title}</span>
                              {isAdmin && (
                                <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                  <button onClick={() => { setEditingSubtaskId(subtask.id); setEditingSubtaskTitle(subtask.title); }} className="text-muted-foreground hover:text-primary transition-colors p-1" data-testid={`rename-subtask-${subtask.id}`}>
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                  <button onClick={() => handleDeleteSubtask(subtask.taskId, subtask.id, subtask.title)} className="text-muted-foreground hover:text-red-500 transition-colors p-1" data-testid={`delete-subtask-${subtask.id}`}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                          {isAdmin ? (
                            <div onClick={e => e.stopPropagation()} className="flex-shrink-0">
                              <Select value={freq} onValueChange={v => handleUpdateSubtaskFrequency(subtask.taskId, subtask.id, v)}>
                                <SelectTrigger className={`h-5 text-[10px] px-1.5 py-0 border ${freqBadge.color} w-auto gap-1`} data-testid={`subtask-freq-${subtask.id}`}>
                                  <SelectValue>{freqBadge.label}</SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="monthly"><span className="text-xs">Monthly</span></SelectItem>
                                  <SelectItem value="quarterly"><span className="text-xs">Quarterly</span></SelectItem>
                                  <SelectItem value="half_yearly"><span className="text-xs">Half Yearly</span></SelectItem>
                                  <SelectItem value="annual"><span className="text-xs">Annual</span></SelectItem>
                                  <SelectItem value="one_time"><span className="text-xs">One Time</span></SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          ) : (
                            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-5 border ${freqBadge.color}`}>{freqBadge.label}</Badge>
                          )}
                          {isAdmin && (
                            <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
                              <Select value={subtask.assigned_to || '_none'} onValueChange={(v) => handleSubtaskStaffAssign(subtask.taskId, subtask.id, v === '_none' ? null : v)}>
                                <SelectTrigger className="h-7 w-[140px] text-[11px] border-dashed" data-testid={`subtask-staff-${subtask.id}`}>
                                  <SelectValue placeholder="Assign Team">{subtask.assigned_to ? getStaffName(subtask.assigned_to) || 'Team' : 'Assign Team'}</SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="_none"><span className="text-xs text-muted-foreground">Unassign</span></SelectItem>
                                  {staffMembers.map(s => (
                                    <SelectItem key={s.id} value={s.id}><span className="text-xs">{s.name || s.email}</span></SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          <Badge variant="secondary" className="text-[11px]"><Users2 className="h-3 w-3 mr-1" />{totalCount}</Badge>
                          {isAdmin && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setReminderTarget({ taskId: subtask.taskId, subtaskId: subtask.id, subtaskTitle: subtask.title }); setReminderDialogOpen(true); }}
                              className="p-1 rounded text-muted-foreground hover:text-amber-600 hover:bg-amber-50 transition-colors"
                              title="Set reminder schedule"
                              data-testid={`set-reminder-${subtask.id}`}
                            >
                              <Bell className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>

                        {isExpanded && (
                          <div className="border-t bg-muted/5">
                            {subtaskClients.length === 0 ? (
                              <div className="py-5 text-center text-sm text-muted-foreground">No clients assigned</div>
                            ) : (subtask.checklist_items || []).length > 0 ? (
                              /* Checklist Grid for Accounting-style subtasks */
                              <div className="p-4">
                                <ChecklistGrid
                                  task={tasks.find(t => t.id === subtask.taskId) || {}}
                                  subtask={subtask}
                                  clients={clients}
                                  staffMembers={staffMembers}
                                  periods={periods}
                                  getPeriodLabel={getPeriodLabel}
                                  canManage={canManage}
                                  isAdmin={isAdmin}
                                  onReload={loadData}
                                />
                              </div>
                            ) : (
                              <>
                              {/* Inline client filter row */}
                              <div className="flex items-center gap-2 px-5 py-2 bg-muted/10 border-b">
                                <Search className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                <Input
                                  placeholder="Filter clients..."
                                  value={expandedFilters[subtask.id]?.client || ''}
                                  onChange={e => setExpandedFilters(prev => ({ ...prev, [subtask.id]: { ...prev[subtask.id], client: e.target.value } }))}
                                  className="h-7 text-xs w-36"
                                  onClick={e => e.stopPropagation()}
                                  data-testid={`expanded-filter-client-${subtask.id}`}
                                />
                                <Select
                                  value={expandedFilters[subtask.id]?.status || 'all'}
                                  onValueChange={v => setExpandedFilters(prev => ({ ...prev, [subtask.id]: { ...prev[subtask.id], status: v === 'all' ? '' : v } }))}
                                >
                                  <SelectTrigger className="h-7 text-[11px] w-32" onClick={e => e.stopPropagation()} data-testid={`expanded-filter-status-${subtask.id}`}>
                                    <SelectValue placeholder="All Statuses" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="all">All Statuses</SelectItem>
                                    {Object.entries(STAGE_LABELS).map(([key, label]) => (
                                      <SelectItem key={key} value={key}><span className="text-xs">{STATUS_SHORT[key]} — {label}</span></SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {(expandedFilters[subtask.id]?.client || expandedFilters[subtask.id]?.status) && (
                                  <button
                                    onClick={e => { e.stopPropagation(); setExpandedFilters(prev => ({ ...prev, [subtask.id]: { client: '', status: '' } })); }}
                                    className="text-muted-foreground hover:text-red-500 p-0.5"
                                    data-testid={`expanded-clear-filters-${subtask.id}`}
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                )}
                                <span className="text-[11px] text-muted-foreground ml-auto">
                                  {(() => {
                                    const f = expandedFilters[subtask.id] || {};
                                    const fq = (f.client || '').trim().toLowerCase();
                                    const filtered = subtaskClients.filter(ce => {
                                      if (fq) {
                                        const cl = clients.find(x => x.id === ce.client_id);
                                        const haystacks = [
                                          (subtask.entity_type === 'director' ? getEntityName(subtask, ce.client_id) : getClientDisplayName(ce.client_id, ce.gstin)) || '',
                                          cl?.name || '', cl?.company_name || '', ce.gstin || '', cl?.pan || '',
                                        ];
                                        if (!haystacks.some(h => h.toLowerCase().includes(fq))) return false;
                                      }
                                      if (f.status) {
                                        const cp = ce.periods || {};
                                        if (!periods.some(p => (cp[p] || 'not_started') === f.status)) return false;
                                      }
                                      return true;
                                    });
                                    return (fq || f.status) ? `${filtered.length} of ${subtaskClients.length}` : `${subtaskClients.length} clients`;
                                  })()}
                                </span>
                              </div>
                              <div className="w-full overflow-x-auto">
                                <div style={{ width: '100%', minWidth: FREQ_MIN_WIDTHS_SM[freq] || FREQ_MIN_WIDTHS_SM.monthly }}>
                                  {/* Header */}
                                  {(() => {
                                    const sort = expandedSorts[subtask.id] || { field: null, dir: null };
                                    const setSort = (next) => setExpandedSorts(prev => ({ ...prev, [subtask.id]: next }));
                                    return (
                                  <div className="flex items-center border-b bg-muted/20 sticky top-0 z-10">
                                    <div className="flex-1 min-w-[256px] px-5 py-2 sticky left-0 bg-muted/20 z-20">
                                      <SortableHeader field="name" sort={sort} onChange={setSort}>{subtask.entity_type === 'director' ? 'Director' : 'Client'}</SortableHeader>
                                    </div>
                                    {canManage && <div className="w-[130px] flex-shrink-0 px-2 py-2 border-l sticky bg-muted/20 z-20 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]" style={{ left: '256px' }}>
                                      <SortableHeader field="staff" sort={sort} onChange={setSort}>Team</SortableHeader>
                                    </div>}
                                    {periods.map(period => {
                                      const dueDate = getDueDate(subtask.title, period, freq, subtask.due_date_rule);
                                      const periodOverdue = isOverdue(dueDate);
                                      const periodDueSoon = isDueSoon(dueDate);
                                      const sortField = `period:${period}`;
                                      return (
                                        <div key={period} className={`flex-shrink-0 text-center py-1.5 border-l ${FREQ_COL_WIDTHS_SM[freq] || FREQ_COL_WIDTHS_SM.monthly}`}>
                                          <SortableHeader field={sortField} sort={sort} onChange={setSort} align="center">
                                            <span className="text-[11px] font-semibold">{getPeriodLabel(period)}</span>
                                          </SortableHeader>
                                          {dueDate && (
                                            <div className={`text-[9px] font-medium mt-0.5 ${dueDateClass(periodOverdue, periodDueSoon, { overdue: 'text-red-500 font-bold', dueSoon: 'text-amber-500', normal: 'text-muted-foreground/60' })}`}>
                                              Due: {formatDueDate(dueDate)}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                    {canManage && <div className="w-8 flex-shrink-0" />}
                                  </div>
                                    );
                                  })()}

                                  {/* Client Rows */}
                                  {(() => {
                                    const f = expandedFilters[subtask.id] || {};
                                    const sort = expandedSorts[subtask.id] || { field: null, dir: null };
                                    const fq = (f.client || '').trim().toLowerCase();
                                    const filtered = subtaskClients.filter(ce => {
                                      const ceDisp = subtask.entity_type === 'director' ? getEntityName(subtask, ce.client_id) : getClientDisplayName(ce.client_id, ce.gstin);
                                      if (fq) {
                                        const cl = clients.find(x => x.id === ce.client_id);
                                        const haystacks = [
                                          ceDisp || '',
                                          cl?.name || '', cl?.company_name || '', ce.gstin || '', cl?.pan || '',
                                        ];
                                        if (!haystacks.some(h => h.toLowerCase().includes(fq))) return false;
                                      }
                                      if (f.status) {
                                        const cp = ce.periods || {};
                                        if (!periods.some(p => (cp[p] || 'not_started') === f.status)) return false;
                                      }
                                      return true;
                                    });
                                    const sorted = (() => {
                                      if (!sort.field || !sort.dir) {
                                        // Default alphabetical sort by client display name (portal-wide rule).
                                        return [...filtered].sort((a, b) => {
                                          const an = (subtask.entity_type === 'director' ? getEntityName(subtask, a.client_id) : getClientDisplayName(a.client_id, a.gstin)) || '';
                                          const bn = (subtask.entity_type === 'director' ? getEntityName(subtask, b.client_id) : getClientDisplayName(b.client_id, b.gstin)) || '';
                                          return an.toLowerCase().localeCompare(bn.toLowerCase());
                                        });
                                      }
                                      const getter = (ce) => {
                                        if (sort.field === 'name') return subtask.entity_type === 'director' ? getEntityName(subtask, ce.client_id) : getClientDisplayName(ce.client_id, ce.gstin);
                                        if (sort.field === 'staff') return getStaffName(ce.assigned_to);
                                        if (sort.field.startsWith('period:')) {
                                          const p = sort.field.slice(7);
                                          return (ce.periods || {})[p] || 'not_started';
                                        }
                                        return null;
                                      };
                                      return sortBy(filtered, getter, sort.dir);
                                    })();
                                    return sorted.map(clientEntry => {
                                    const clientPeriods = clientEntry.periods || {};
                                    const ceDisplayName = subtask.entity_type === 'director'
                                      ? getEntityName(subtask, clientEntry.client_id)
                                      : getClientDisplayName(clientEntry.client_id, clientEntry.gstin);
                                    return (
                                      <div key={clientEntry.gstin ? `${clientEntry.client_id}|${clientEntry.gstin}` : clientEntry.client_id} className="flex items-center border-b last:border-b-0 hover:bg-muted/10 transition-colors">
                                        <div className="flex-1 min-w-[256px] px-5 py-2 flex items-center gap-2 sticky left-0 bg-background z-10">
                                          <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-medium text-primary flex-shrink-0">
                                            {ceDisplayName?.[0]?.toUpperCase()}
                                          </div>
                                          <span className="text-sm flex-1 min-w-0" title={ceDisplayName}>{ceDisplayName}</span>
                                          <button onClick={(e) => { e.stopPropagation(); handleOpenTaskChat(clientEntry.client_id); }} className="text-muted-foreground hover:text-primary transition-colors p-0.5 flex-shrink-0" title="Open chat">
                                            <MessageSquare className="h-3 w-3" />
                                          </button>
                                        </div>
                                        {canManage && (
                                          <div className="w-[130px] flex-shrink-0 px-1 py-1 border-l sticky bg-background z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]" style={{ left: '256px' }}>
                                            {isAdmin ? (
                                              <Select value={clientEntry.assigned_to || '_none'} onValueChange={(v) => handleClientStaffAssign(subtask.taskId, subtask.id, clientEntry.client_id, v === '_none' ? null : v)}>
                                                <SelectTrigger className="h-7 w-full text-[11px] border-dashed px-2" data-testid={`client-staff-${clientEntry.client_id}`}>
                                                  <SelectValue>{clientEntry.assigned_to ? (getStaffName(clientEntry.assigned_to) || 'Team') : '—'}</SelectValue>
                                                </SelectTrigger>
                                                <SelectContent>
                                                  <SelectItem value="_none"><span className="text-xs text-muted-foreground">Unassign</span></SelectItem>
                                                  {staffMembers.map(s => (
                                                    <SelectItem key={s.id} value={s.id}><span className="text-xs">{s.name || s.email}</span></SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                            ) : (
                                              <span className="text-[11px] text-muted-foreground px-2">{clientEntry.assigned_to ? (getStaffName(clientEntry.assigned_to) || 'Team') : '—'}</span>
                                            )}
                                          </div>
                                        )}
                                        {periods.map(period => {
                                          const status = clientPeriods[period] || 'not_started';
                                          const dueDate = getDueDate(subtask.title, period, freq, subtask.due_date_rule);
                                          const cellOverdue = isOverdue(dueDate) && status !== 'filed';
                                          const cellDueSoon = isDueSoon(dueDate) && status !== 'filed';
                                          const ceGstin = clientEntry.gstin || null;
                                          const gstinObj = getClientGstinObj(clientEntry.client_id, ceGstin);
                                          const isInactive = ceGstin && gstinObj && !isPeriodActiveForGstin(period, freq, gstinObj);
                                          const inactiveTip = isInactive ? inactiveReason(period, freq, gstinObj) : '';
                                          return (
                                            <div key={period} className={`flex-shrink-0 border-l px-1 py-1.5 ${FREQ_COL_WIDTHS_SM[freq] || FREQ_COL_WIDTHS_SM.monthly} ${isInactive ? 'bg-muted/30' : dueDateClass(cellOverdue, cellDueSoon, { overdue: 'bg-red-50', dueSoon: 'bg-amber-50/30', normal: '' })}`}>
                                              {isInactive ? (
                                                <div
                                                  className="h-7 flex items-center justify-center text-[11px] text-muted-foreground/40 rounded cursor-not-allowed select-none"
                                                  title={inactiveTip}
                                                >
                                                  —
                                                </div>
                                              ) : canManage ? (
                                                <Select value={status} onValueChange={(v) => handlePeriodStatusChange(subtask.taskId, subtask.id, clientEntry.client_id, period, v)}>
                                                  <SelectTrigger className={`h-7 w-full text-[11px] font-bold px-1 justify-center ${dueDateClass(cellOverdue, cellDueSoon, { overdue: 'border border-red-300 ring-1 ring-red-200', dueSoon: 'border border-amber-300', normal: 'border-0' })} ${STATUS_CELL_COLORS[status]}`} data-testid={`period-status-${period}`}>
                                                    <SelectValue>{STATUS_SHORT[status]}</SelectValue>
                                                  </SelectTrigger>
                                                  <SelectContent>
                                                    {(subtask.custom_stages || DEFAULT_STAGES).map(key => (
                                                      <SelectItem key={key} value={key}><span className="text-xs">{STATUS_SHORT[key] || key} — {STAGE_LABELS[key] || key}</span></SelectItem>
                                                    ))}
                                                  </SelectContent>
                                                </Select>
                                              ) : (
                                                <div className={`h-7 flex items-center justify-center text-[11px] font-bold rounded ${cellOverdue ? 'ring-1 ring-red-300' : ''} ${STATUS_CELL_COLORS[status] || 'bg-gray-50 text-gray-400'}`}>{STATUS_SHORT[status] || status}</div>
                                              )}
                                            </div>
                                          );
                                        })}
                                        {isAdmin && (
                                          <div className="w-8 flex-shrink-0 flex items-center justify-center">
                                            <button onClick={(e) => { e.stopPropagation(); handleClientToggle(subtask.taskId, subtask, clientEntry.client_id); }} className="text-muted-foreground hover:text-red-500 transition-colors p-0.5">
                                              <X className="h-3.5 w-3.5" />
                                            </button>
                                          </div>
                                        )}
                                        {!isAdmin && canManage && <div className="w-8 flex-shrink-0" />}
                                      </div>
                                    );
                                  })
                                })()}
                                </div>
                              </div>
                              </>
                            )}

                            {/* Manage clients (opens modal) */}
                            {canManage && (() => {
                              const ONE_TIME_KINDS = { 'gst registration': 'registration', 'gst cancellation': 'cancellation', 'gst amendment': 'amendment', 'gst notice': 'notice' };
                              const oneTimeKind = (board?.name === 'GST One-Time Activities') ? ONE_TIME_KINDS[(subtask.title || '').toLowerCase()] : null;
                              if (oneTimeKind) {
                                // Phase 2 — open kind-aware Add Instance dialog (shows GSTIN dropdown
                                // filtered to selected client, supports prospect_name for new registrations)
                                return (
                                  <div className="px-5 py-2 border-t bg-muted/5 flex items-center gap-3">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setOneTimeDialog({ taskId: subtask.taskId, subtaskId: subtask.id, kind: oneTimeKind }); }}
                                      className="inline-flex items-center gap-1.5 text-xs text-primary hover:bg-primary/10 transition-colors px-2 py-1 rounded-lg border border-primary/30"
                                      data-testid="board-add-one-time-instance"
                                    >
                                      <Plus className="h-3 w-3" /> Add {oneTimeKind === 'notice' ? 'Notice' : oneTimeKind === 'registration' ? 'Registration' : oneTimeKind === 'cancellation' ? 'Cancellation' : 'Amendment'}
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/boards/${boardId}/task/${subtask.taskId}/subtask/${subtask.id}`); }}
                                      className="text-[11px] text-muted-foreground hover:text-primary underline-offset-2 hover:underline"
                                    >
                                      Open full view →
                                    </button>
                                    <span className="ml-auto text-[11px] text-muted-foreground">{subtaskClients.length} instance{subtaskClients.length === 1 ? '' : 's'}</span>
                                  </div>
                                );
                              }
                              return (
                                <div className="px-5 py-2 border-t bg-muted/5">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setClientPickerOpen({ taskId: subtask.taskId, subtaskId: subtask.id });
                                    }}
                                    className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors px-2 py-1 rounded-lg hover:bg-muted border border-dashed"
                                    data-testid="add-client-to-subtask"
                                  >
                                    <Users2 className="h-3 w-3" /> Manage clients
                                  </button>
                                  <span className="ml-3 text-[11px] text-muted-foreground">{subtaskClients.length} linked</span>
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {tasks.length === 0 && (
        <Card><CardContent className="p-8 text-center space-y-3">
          <p className="text-muted-foreground">No tasks in this board yet.</p>
          {isAdmin && board?.name?.toLowerCase() === 'accounting' && (
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const res = await tasksAPI.seedAccounting();
                  const data = res.data;
                  if (data.status === 'created') {
                    toast.success(`Created Accounting task with ${data.clients_linked} clients`);
                    await loadData();
                  } else {
                    toast.info(data.reason || 'Already seeded');
                    await loadData();
                  }
                } catch (err) { toast.error('Failed to seed accounting task'); }
              }}
              data-testid="seed-accounting-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Create Accounting Task (Monthly + Annual checklists)
            </Button>
          )}
        </CardContent></Card>
      )}

      {reminderDialogOpen && reminderTarget && (
        <ReminderDialog
          open={reminderDialogOpen}
          onOpenChange={setReminderDialogOpen}
          taskId={reminderTarget.taskId}
          subtaskId={reminderTarget.subtaskId}
          subtaskTitle={reminderTarget.subtaskTitle}
        />
      )}

      <ClientPickerDialog
        open={!!clientPickerOpen}
        onOpenChange={(v) => { if (!v) setClientPickerOpen(null); }}
        clients={clients}
        linkedIds={(() => {
          if (!clientPickerOpen) return [];
          const task = tasks.find(t => t.id === clientPickerOpen.taskId);
          const st = task?.subtasks?.find(s => s.id === clientPickerOpen.subtaskId);
          return (st?.clients || []).map(c => c.client_id);
        })()}
        onApply={handleApplyClientPickerBoard}
        title={(() => {
          if (!clientPickerOpen) return 'Manage clients';
          const task = tasks.find(t => t.id === clientPickerOpen.taskId);
          const st = task?.subtasks?.find(s => s.id === clientPickerOpen.subtaskId);
          return `Manage clients for ${st?.title || 'subtask'}`;
        })()}
        subtitle="Tick to add, untick to remove. Click Apply changes when done."
      />

      {/* Phase 2 — kind-aware Add Instance dialog for GST One-Time Activities */}
      {oneTimeDialog && (
        <OneTimeInstanceDialog
          open={!!oneTimeDialog}
          onOpenChange={(v) => { if (!v) setOneTimeDialog(null); }}
          taskId={oneTimeDialog.taskId}
          subtaskId={oneTimeDialog.subtaskId}
          kind={oneTimeDialog.kind}
          clients={clients}
          instance={null}
          onSaved={() => loadData()}
        />
      )}
    </div>
  );
};

export default BoardDetailPage;
